import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { useSkills } from '@/hooks/api/useSkills';
import { Skill } from '@/types';
import { toast } from 'sonner';
import { Plus, Zap, Lightbulb, Ligature } from 'lucide-react';
import { useDialogManager } from '@/hooks/useDialogManager';
import { SkillDetails } from './SkillDetails';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export const SkillSettings = () => {
  const { data: skills, isLoading } = useSkills();
  const { openDialog } = useDialogManager();

  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const handleSelectSkill = (skill: Skill) => {
    setSelectedSkill(skill);
  };

  const handleAddNew = () => {
    setSelectedSkill(null);
    setIsCreating(true);
  };

  const handleCancel = () => {
    setSelectedSkill(null);
    setIsCreating(false);
  };

  const handleSave = (data: Skill) => {
    if (selectedSkill) {
      // updateSkill({ ...selectedSkill, ...data });

      toast.success(`Skill "${data.name}" updated.`);
    } else {
      // addSkill(data);

      toast.success(`Skill "${data.name}" created.`);
    }
    handleCancel();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start h-full">
      <div className="md:col-span-1">
        <Card className="border-border shadow-sm overflow-hidden h-full max-h-[calc(100vh-12rem)] flex flex-col">
          <CardHeader className="px-4 py-3 border-b border-border/50 bg-muted/30 flex flex-row items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-muted-foreground text-secondary" />
              <CardTitle className="text-md font-semibold text-foreground">
                Skills
              </CardTitle>
            </div>
            <Button
              size="icon"
              variant="outline"
              className="h-7 w-7 text-muted-foreground hover:text-primary hover:bg-primary/10"
              onClick={() => openDialog('create-skill')}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="p-2 overflow-y-auto flex-1">
            <div className="space-y-1">
              {isLoading
                ? [1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-3">
                      <Skeleton className="h-4 w-4 rounded-sm" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                  ))
                : skills?.map((skill) => {
                    const isActive = selectedSkill?.id === skill.id;
                    return (
                      <button
                        key={skill.id}
                        onClick={() => handleSelectSkill(skill)}
                        className={cn(
                          'w-full text-left flex items-center justify-between px-3 py-3 text-sm font-medium transition-all rounded-r-md group cursor-pointer',
                          'border-l-[3px]',
                          isActive
                            ? 'border-l-secondary bg-primary/5 text-foreground'
                            : 'border-l-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <Lightbulb
                            className={cn(
                              'h-4 w-4 transition-colors',
                              isActive
                                ? 'text-secondary'
                                : 'text-muted-foreground group-hover:text-foreground',
                            )}
                          />
                          <span className="truncate">{skill.name}</span>
                        </div>
                      </button>
                    );
                  })}

              {!isLoading && skills?.length === 0 && (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  No skills found.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* --- Detail View for Form --- */}
      <main className="md:col-span-2">
        {selectedSkill ? (
          <SkillDetails key={selectedSkill!.id} skill={selectedSkill!} />
        ) : (
          <div className="flex flex-col items-center justify-center h-[400px] rounded-lg border-2 border-dashed border-border bg-muted/10 animate-in fade-in zoom-in-95 duration-300">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <Zap className="h-8 w-8 text-muted-foreground/50" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-foreground">
              No Selection
            </h3>
            <p className="mb-4 text-center text-sm text-muted-foreground max-w-xs">
              Select a skill from the list to view details or add a new skill.
            </p>
            <Button
              variant="outline"
              onClick={() => openDialog('create-skill')}
              className="gap-2"
            >
              <Plus className="h-4 w-4" /> Create Skill
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};
